
// 3/1 optimizations:
// useMessageHistory.js
import { useState, useEffect, useRef } from 'react';
import { fetchMessagesForConversation, subscribeToMessages } from '../utils/db-messaging';

export function useMessageHistory(userId, conversationId = null, filterFunction = null) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const initialLoadCompleteRef = useRef(false);
  const requestRef = useRef(null);
  const isProcessingUpdateRef = useRef(false);

  useEffect(() => {
    if (!userId || !conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    // Cancel any pending animation frames
    if (requestRef.current) {
      cancelAnimationFrame(requestRef.current);
      requestRef.current = null;
    }

    const loadMessages = async () => {
      try {
        // First load initial messages
        const initialMessages = await fetchMessagesForConversation(userId, conversationId);
        
        if (isMounted) {
          // Use requestAnimationFrame for smoother updates
          requestRef.current = requestAnimationFrame(() => {
            if (!isMounted) return;
            
            // Apply filter if provided
            const messagesToSet = filterFunction ? filterFunction(initialMessages) : initialMessages;
            setMessages(messagesToSet);
            setLoading(false);
            initialLoadCompleteRef.current = true;
          });
        }

        // Clean up existing subscription
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }

        // Defer subscription setup to reduce rendering conflicts
        setTimeout(() => {
          if (!isMounted) return;
          
          // Set up real-time subscription after a delay
          const unsubscribe = subscribeToMessages(userId, conversationId, (newMessages) => {
            if (!isMounted || isProcessingUpdateRef.current) return;
            
            // Check if messages are actually different to avoid unnecessary renders
            const areMessagesDifferent = () => {
              if (messages.length !== newMessages.length) return true;
              
              // Compare message IDs to see if anything has changed
              const currentIds = new Set(messages.map(m => m.id));
              return newMessages.some(m => !currentIds.has(m.id));
            };
            
            if (initialLoadCompleteRef.current && areMessagesDifferent()) {
              // Set processing flag to avoid nested updates
              isProcessingUpdateRef.current = true;
              
              // Use requestAnimationFrame for smoother updates
              requestRef.current = requestAnimationFrame(() => {
                if (!isMounted) return;
                
                // Apply filter if provided
                const messagesToSet = filterFunction ? filterFunction(newMessages) : newMessages;
                setMessages(messagesToSet);
                
                // Clear processing flag after update
                isProcessingUpdateRef.current = false;
              });
            }
          });

          unsubscribeRef.current = unsubscribe;
        }, 1000); // 1 second delay to allow initial render to complete

      } catch (err) {
        console.error('Error in useMessageHistory:', err);
        if (isMounted) {
          setError(err);
          setLoading(false);
        }
      }
    };

    loadMessages();

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = null;
      }
    };
  }, [userId, conversationId, filterFunction]);

  // Method to manually update messages without waiting for Firebase
  const updateMessages = (newMessages) => {
    setMessages(newMessages);
  };

  return { messages, updateMessages, loading, error };
}

